import { useEffect, useState } from 'react';
import { fetchFolder, GalleryApiError, GalleryFolder } from '../lib/gallery/api';

// Prosty cache w pamięci (serwer też cache'uje listę przez 30 s).
const TTL_MS = 60 * 1000;

interface CacheEntry {
    at: number;
    data: GalleryFolder;
}

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<GalleryFolder>>();

const fresh = (path: string): GalleryFolder | null => {
    const entry = cache.get(path);
    return entry && Date.now() - entry.at < TTL_MS ? entry.data : null;
};

export function loadGalleryFolder(path: string): Promise<GalleryFolder> {
    const hit = fresh(path);
    if (hit) return Promise.resolve(hit);

    const running = inflight.get(path);
    if (running) return running;

    const request = fetchFolder(path)
        .then((data) => {
            cache.set(path, { at: Date.now(), data });
            return data;
        })
        .finally(() => {
            inflight.delete(path);
        });
    inflight.set(path, request);
    return request;
}

/** Podgrzewa cache (np. po najechaniu na album); błędy są ignorowane. */
export function prefetchGalleryFolder(path: string): void {
    loadGalleryFolder(path).catch(() => undefined);
}

export type GalleryFolderState =
    | { status: 'loading' }
    | { status: 'ready'; data: GalleryFolder }
    | { status: 'error'; error: GalleryApiError; retry: () => void };

type Stored =
    | { key: string; status: 'ready'; data: GalleryFolder }
    | { key: string; status: 'error'; error: GalleryApiError };

export function useGalleryFolder(path: string): GalleryFolderState {
    const [stored, setStored] = useState<Stored | null>(() => {
        const hit = fresh(path);
        return hit ? { key: path, status: 'ready', data: hit } : null;
    });
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;

        const hit = fresh(path);
        if (hit) {
            setStored({ key: path, status: 'ready', data: hit });
            return;
        }

        setStored(null);
        loadGalleryFolder(path)
            .then((data) => {
                if (!cancelled) setStored({ key: path, status: 'ready', data });
            })
            .catch((error) => {
                if (cancelled) return;
                setStored({
                    key: path,
                    status: 'error',
                    error: error instanceof GalleryApiError ? error : new GalleryApiError('Nie udało się wczytać albumu.', 0),
                });
            });

        return () => {
            cancelled = true;
        };
    }, [path, attempt]);

    // Stan z poprzedniej ścieżki nie może „przeciekać” na nową.
    if (!stored || stored.key !== path) return { status: 'loading' };
    if (stored.status === 'ready') return { status: 'ready', data: stored.data };
    return { status: 'error', error: stored.error, retry: () => setAttempt((n) => n + 1) };
}
